import 'styled-components/macro';

import React, { useEffect, useState } from 'react';
import {
    Button,
    TextField,
    Typography,
    styled,
    Divider,
    Box,
    Checkbox,
    ErrorState,
    LoadingState,
} from '@harver/shared-ui';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

import { defaultTheme as theme } from '../../branding/theme';
import { ContentCard } from '../../components/ContentCard';
import { getRecaptchaVerificationFailedError, validate } from '../../pages/openInvite/helpers';
import { createOpenInvite, getHpAccountIdForOpenInvite, getOpenInviteDetails } from '../../pages/openInvite/service';
import { OpenInvite } from '../shared/views/OpenInvite';
import { useContent } from '../../branding/content.context';
import { SETTINGS_ERRORS, OPEN_INVITE_ERRORS } from '../shared/domains';
import { OpenInviteSuccess } from '../shared/views/openInvitePages/OpenInviteSuccess';
import { OpenInviteLinkDisabled } from '../shared/views/openInvitePages/OpenInviteLinkDisabled';
import { OpenInviteAlreadyRegistered } from '../shared/views/OpenInviteAlreadyRegistered';
import CookieBanner from '../../components/CookieBanner';
import TooManyRequests from '../shared/views/openInvitePages/TooManyRequests';
import { isEnabled } from '../../HarverPages/shared/services';
import { isRecaptchaVerificationFailed } from '../../utils/helpers';

const StyledDivider = styled(Divider)`
    margin-top: 2rem;
    margin-bottom: 2rem;
`;
const StyledErrorMessage = styled(ErrorMessage)`
    color: ${({ theme }) => theme.colors.error['500']};
    margin-top: ${({ theme }) => theme.sizes.xxSmall};
`;
const initialDefaultValues = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    termsAndCondition: false,
    recaptchaToken: '',
};

export interface OpenInvite {
    firstName: string;
    lastName: string;
    emailAddress: string;
    termsAndCondition: boolean;
    recaptchaToken: string;
}
const Error = ({ name }) => {
    return <StyledErrorMessage name={name} component={Typography} />;
};

const OpenInviteDetails = (): JSX.Element => {
    const [data] = useState<OpenInvite>(initialDefaultValues);
    const [checkBoxError, setCheckBoxError] = useState<boolean>(false);
    const { openInviteId } = useParams() as any;
    const [openInviteStatus, setOpenInviteStatus] = useState<string>('');
    const [errorCode, setErrorCode] = useState('');
    const [btnDisable, setBtnDisable] = useState<boolean>(false);
    const { openInviteRegistration: LANG, loading: LOADING } = useContent();
    const [isInitialLoading, setInitialLoading] = useState(true);
    const [organizationName, setOrganizationName] = useState<string>('');

    //TODO - will remove after removing feature toggle - schedulingOpenInviteReCaptcha
    const [featureToggle, setFeatureToggle] = useState<boolean>(false);
    const [captcha, setCaptcha] = useState<ReCAPTCHA>();
    const [recaptchaVerificationFailed, setRecaptchaVerificationFailed] = useState<boolean>();

    const validateForCheckBox = async () => {
        setCheckBoxError(true);
    };

    const getSettings = async (openInviteId) => {
        await getOpenInviteDetails(openInviteId)
            .then((resp) => {
                setOrganizationName(resp.organizationName);
                //TODO - will remove after removing feature toggle - schedulingOpenInviteReCaptcha
                getHpAccoutId(resp.organizationId);
            })
            .catch((err) => {
                if (err?.response?.status === 429) {
                    setErrorCode(OPEN_INVITE_ERRORS.tooManyRequests);
                    return;
                }
                const errorCode = err?.response?.data?.context?.code;
                if (errorCode === SETTINGS_ERRORS.settingDisabled || errorCode === SETTINGS_ERRORS.openInviteDisabled) {
                    setOpenInviteStatus('DISABLED');
                }
            })
            .finally(() => {
                setInitialLoading(false);
            });
    };

    const getHpAccoutId = async (organizationId) => {
        const hpAccountIdForOrganization = await getHpAccountIdForOpenInvite(organizationId);
        const schedulingOpenInviteReCaptcha = await isEnabled(
            'schedulingOpenInviteReCaptcha',
            hpAccountIdForOrganization,
        );
        setFeatureToggle(schedulingOpenInviteReCaptcha);
    };
    useEffect(() => {
        getSettings(openInviteId);
    }, [openInviteId]);

    const handleSubmit = async (values) => {
        setBtnDisable(true);
        setRecaptchaVerificationFailed(false);
        try {
            const payload = {
                candidate: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.emailAddress,
                },
            };
            await createOpenInvite(openInviteId, payload, values.recaptchaToken);
            setOpenInviteStatus('SUCCESS');
        } catch (error: any) {
            if (error?.response?.status === 429) {
                setErrorCode(OPEN_INVITE_ERRORS.tooManyRequests);
                return;
            }

            const errorCode = error?.response?.data?.context?.code || OPEN_INVITE_ERRORS.somethingWentWrong;

            if (isRecaptchaVerificationFailed(errorCode)) {
                captcha ? captcha.reset() : '';
                setRecaptchaVerificationFailed(true);
                setBtnDisable(false);
            }

            setErrorCode(errorCode);
        }
    };

    const recaptchaVerificationFailedError = () =>
        recaptchaVerificationFailed ? getRecaptchaVerificationFailedError(LANG.validate) : '';

    if (isInitialLoading) return <LoadingState description={LOADING.p} testId="openInvite.form.loading" />;

    if (errorCode === OPEN_INVITE_ERRORS.alreadyRegistered) {
        return <OpenInviteAlreadyRegistered />;
    } else if (errorCode === OPEN_INVITE_ERRORS.tooManyRequests) {
        return <TooManyRequests />;
    } else if (errorCode === OPEN_INVITE_ERRORS.somethingWentWrong) {
        document.title = 'Something went wrong';
        return <ErrorState testId="openInvite.errorScreen" onReloadClick={() => window.location.reload()} />;
    }

    if (openInviteStatus === 'SUCCESS') {
        return <OpenInviteSuccess />;
    }

    if (openInviteStatus === 'DISABLED') {
        return <OpenInviteLinkDisabled />;
    }

    return (
        <ContentCard>
            <Formik
                initialValues={data}
                validate={(value) => validate(value, LANG.validate, featureToggle)}
                enableReinitialize={true}
                onSubmit={handleSubmit}
            >
                {() => (
                    <Form
                        name="openInviteForm"
                        data-testid="open-invite-form"
                        css={{
                            margin: `${theme.sizes.xxLarge} 0`,
                        }}
                    >
                        <Typography
                            css={{
                                textAlign: 'center',
                                marginBottom: theme.sizes.medium,
                            }}
                            testId="title"
                            variant="h2"
                        >
                            {LANG.title.replace('{ClientName}', organizationName)}
                        </Typography>
                        <Typography
                            css={{
                                textAlign: 'center',
                                marginBottom: theme.sizes.large,
                            }}
                            variant="body1"
                            testId="discription"
                        >
                            {LANG.p1}
                        </Typography>
                        <StyledDivider />
                        <Box
                            sx={{
                                marginRight: theme.getSpacing(6),
                                marginLeft: theme.getSpacing(6),
                            }}
                        >
                            <Field name="firstName">
                                {({ field }) => (
                                    <TextField
                                        size="medium"
                                        value={field.value}
                                        onChange={field.onChange(field.name)}
                                        testId="first-name-input"
                                        variant="filled"
                                        label={LANG.firstName}
                                        maxLength={75}
                                    />
                                )}
                            </Field>
                            <Error name="firstName" />
                        </Box>

                        <Box
                            sx={{
                                marginRight: theme.getSpacing(6),
                                marginLeft: theme.getSpacing(6),
                                marginTop: theme.getSpacing(5),
                            }}
                        >
                            <Field name="lastName">
                                {({ field }) => (
                                    <TextField
                                        size="medium"
                                        value={field.value}
                                        onChange={field.onChange(field.name)}
                                        testId="last-name-input"
                                        variant="filled"
                                        label={LANG.lastName}
                                        maxLength={75}
                                    />
                                )}
                            </Field>
                            <Error name="lastName" />
                        </Box>

                        <Box
                            sx={{
                                marginRight: theme.getSpacing(6),
                                marginLeft: theme.getSpacing(6),
                                marginTop: theme.getSpacing(5),
                            }}
                        >
                            <Field name="emailAddress">
                                {({ field }) => (
                                    <TextField
                                        size="medium"
                                        value={field.value}
                                        onChange={field.onChange(field.name)}
                                        variant="filled"
                                        label={LANG.email}
                                        testId="email-input"
                                        maxLength={75}
                                    />
                                )}
                            </Field>
                            <Error name="emailAddress" />
                        </Box>

                        <Box
                            sx={{
                                width: theme.getSpacing(100),
                                marginRight: theme.getSpacing(6),
                                marginLeft: theme.getSpacing(6),
                                marginTop: theme.getSpacing(5),
                            }}
                        >
                            <div
                                css={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'left',
                                    alignItems: 'start',
                                }}
                            >
                                <Field name="termsAndCondition">
                                    {({ field }) => (
                                        <Checkbox
                                            testId="terms-and-conditions"
                                            onChange={field.onChange(field.name)}
                                            css={{ color: !checkBoxError ? '' : 'red' }}
                                        />
                                    )}
                                </Field>
                                <Typography
                                    variant="body2"
                                    css={{
                                        marginTop: '7px',
                                    }}
                                >
                                    {LANG.termsAndConditions.replace(/{ClientName}/g, organizationName)}
                                </Typography>
                            </div>
                            <Error name="termsAndCondition" />
                        </Box>
                        {featureToggle ? (
                            <>
                                <Box>
                                    <div
                                        css={{
                                            flex: 1,
                                            display: 'flex',
                                            marginTop: '20px',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Field name="recaptchaToken">
                                            {({ field }) => (
                                                <ReCAPTCHA
                                                    ref={setCaptcha}
                                                    data-testid="recaptcha-status"
                                                    sitekey={`${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`}
                                                    onChange={field.onChange(field.name)}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div
                                        css={{
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Error name="recaptchaToken" />
                                    </div>
                                    {recaptchaVerificationFailedError()}
                                </Box>
                            </>
                        ) : (
                            ''
                        )}
                        <div
                            css={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '20px',
                            }}
                        >
                            <Button
                                type="submit"
                                color="primary"
                                testId="continue-with-openInvite"
                                onClick={validateForCheckBox}
                                disabled={btnDisable}
                            >
                                {LANG.button}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
            <CookieBanner />
        </ContentCard>
    );
};

export default OpenInviteDetails;
